import type {AxiosResponse} from "axios";

import ResourceApi from "@/src/modules/core/api/resource-api";
import {axios, getParams} from "@/src/modules/core/lib";
import type {GetListResponse, GetResponse, ParamsOptions} from "@/src/modules/core/types";

import type {BriefingBoardPendingView} from "../types";

const PATH = "briefingboard";

type PaginatedParams = {
  page?: {limit: number; offset: number};
};

type InboxParams = PaginatedParams & {
  unread?: boolean;
  q?: string;
};

type PendingParams = PaginatedParams & {
  shift_id?: number;
  /** `gate` → BriefingBoardGateResource (brief + media, no unused fields). */
  view?: BriefingBoardPendingView;
};

function appendPageParams(searchParams: URLSearchParams, page?: PaginatedParams["page"]) {
  if (page) {
    searchParams.append("page[limit]", String(page.limit));
    searchParams.append("page[offset]", String(page.offset));
  }
}

function getInboxParams(params?: InboxParams) {
  const searchParams = new URLSearchParams();
  if (params?.unread) {
    searchParams.append("unread", "1");
  }
  const q = params?.q?.trim();
  if (q) {
    searchParams.append("q", q);
  }
  appendPageParams(searchParams, params?.page);
  return searchParams;
}

function getPendingParams(params?: PendingParams) {
  const searchParams = new URLSearchParams();
  if (params?.shift_id != null) {
    searchParams.append("shift_id", String(params.shift_id));
  }
  if (params?.view != null && params.view !== "default") {
    searchParams.append("view", params.view);
  }
  appendPageParams(searchParams, params?.page);
  return searchParams;
}

class BriefingBoardsApi extends ResourceApi {
  constructor() {
    super(PATH);
  }

  searchManagement(
    options: ParamsOptions,
  ): Promise<AxiosResponse<GetListResponse<unknown>>> {
    return this.search(options);
  }

  getInbox(params?: InboxParams): Promise<AxiosResponse<GetListResponse<unknown>>> {
    return axios({
      url: `${PATH}/inbox`,
      method: "get",
      params: getInboxParams(params),
    });
  }

  getPending(params?: PendingParams): Promise<AxiosResponse<GetListResponse<unknown>>> {
    return axios({
      url: `${PATH}/pending`,
      method: "get",
      params: getPendingParams(params),
    });
  }

  getRecipients(
    id: number,
    options: ParamsOptions,
  ): Promise<AxiosResponse<GetListResponse<unknown>>> {
    return axios({
      url: `${PATH}/${id}/recipients`,
      method: "get",
      params: getParams(options),
    });
  }

  markRead(
    id: number,
    data: {shift_id: number},
  ): Promise<AxiosResponse<GetResponse<unknown>>> {
    return this.post(`${PATH}/${id}/read`, data);
  }
}

export const briefingBoardsApi = new BriefingBoardsApi();
